// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./PhaseConsole.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";

// MUI ICONS
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import PhaseConsoleEditAddDialog from "./PhaseConsoleEditAddDialog/PhaseConsoleEditAddDialog";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { convertToPST } from "../../../constants/dateTime";
import {
	checkboxSort,
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const PhaseConsole = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [phases, setPhases] = useState([]);

	const [phaseRows, setPhaseRows] = useState([]);
	const [phaseColumns, setPhaseColumns] = useState([]);

	const [properties, setProperties] = useState([]);
	const [projects, setProjects] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [phaseTypes, setPhaseTypes] = useState([]);
	const [phaseNames, setPhaseNames] = useState([]);

	const [recordToEdit, setRecordToEdit] = useState({});
	const [loading, setLoading] = useState(false);
	const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
	const [editing, setEditing] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Phase Console",
			employeeID: employeeID,
			notes: "Accessed the Phase Console page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		handleAPICalls();
	}, []);

	useEffect(() => {
		if (
			phases.length > 0 &&
			properties.length > 0 &&
			projects.length > 0 &&
			phaseTypes.length > 0 &&
			phaseNames.length > 0
		) {
			createRowData();
			createColumnData();
		}
	}, [phases, properties, projects, phaseTypes, phaseNames]);

	// INPUT HANDLERS

	const handleAddClick = () => {
		setEditing(false);
		setEditAddDialogOpen(true);
	};

	const handleEditClick = (record) => {
		setRecordToEdit(record);
		setEditing(true);
		setEditAddDialogOpen(true);
	};

	const handleEditAddDialogClose = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
		handleAPICalls();
	};

	const handleEditAddDialogCloseNoRefresh = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
	};

	// API FUNCTIONS

	const handleAPICalls = async () => {
		setLoading(true);
		await fetchPhases();
		await fetchProperties();
		await fetchProjects();
		await fetchEmployees();
		await fetchPhaseTypes();
		await fetchPhaseNames();
		setLoading(false);
	};

	const fetchPhases = async (sortBy, sortDirection) => {
		let queryParams;
		if (sortBy && sortDirection) {
			queryParams = `sortBy=${sortBy}&sortDirection=${sortDirection}`;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				`/api/jobs/phases/all?${queryParams}`
			);
			setPhases(res.data);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchProperties = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/property/all"
			);
			setProperties(res.data);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchProjects = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/project/all"
			);
			setProjects(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const fetchEmployees = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/all"
			);
			setEmployees(res.data);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchPhaseTypes = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/phaseTypes/all"
			);
			setPhaseTypes(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const fetchPhaseNames = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/phases/names/all"
			);
			setPhaseNames(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		setPhaseColumns([
			{
				accessorKey: "property_name",
				header: () => <span>Property</span>,
				label: "Property",
				cell: ({ row }) => (
					<span>{row.original.property_name || notApplicable}</span>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: properties.map((property) => ({
					label: property.name,
					value: property.name,
				})),
			},
			{
				accessorKey: "project_name",
				header: () => <span>Project</span>,
				label: "Project",
				cell: ({ row }) => (
					<span>{row.original.project_name || notApplicable}</span>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: projects.map((project) => ({
					label: project.name,
					value: project.name,
				})),
			},
			{
				accessorKey: "name",
				header: () => <span>Phase Name</span>,
				label: "Phase Name",
				cell: ({ row }) => (
					<span>{row.original.name || notApplicable}</span>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: phaseNames.map((phaseName) => ({
					label: phaseName.name,
					value: phaseName.name,
				})),
			},
			{
				accessorKey: "phase_type_name",
				header: () => <span>Phase Type</span>,
				label: "Phase Type",
				cell: ({ row }) => (
					<span>{row.original.phase_type_name || notApplicable}</span>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: phaseTypes.map((phaseType) => ({
					label: phaseType.name,
					value: phaseType.name,
				})),
			},
			{
				accessorKey: "dropbox_path",
				header: () => <span>Dropbox Path</span>,
				label: "Dropbox Path",
				cell: ({ row }) => (
					<span>{row.original.dropbox_path || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				text: "Owner",
				accessorKey: "owner",
				header: () => <span>Owner</span>,
				label: "Owner",
				cell: ({ row }) => (
					<span>{row.original.owner || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "created",
				header: () => <span>Created</span>,
				label: "Created",
				cell: ({ row }) => (
					<span>{row.original.created || notApplicable}</span>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: ({ row }) => (
					<IconButton
						onClick={() => handleEditClick(row.original.record)}
						disableRipple
						sx={{ justifyContent: "flex-end" }}
					>
						<EditIcon fontsize="small" />
					</IconButton>
				),
				enableColumnFilter: false,
				enableSorting: false,
			},
		]);
	};

	const createRowData = () => {
		setPhaseRows(
			phases.map((phase) => ({
				record: phase,
				property_name: phase.property_name,
				project_name: phase.project_name,
				name: phase.name,
				phase_type_name: phase.phase_type_name,
				dropbox_path: phase.dropbox_path,
				owner:
					phase.owner_first_name && phase.owner_last_name
						? phase.owner_first_name + " " + phase.owner_last_name
						: null,
				created: convertToPST(phase.created),
			}))
		);
	};

	// RENDER
	return (
		<div className="PhaseConsole">
			<div className="PhaseConsoleHeader">
				<div className="PhaseConsoleHeaderTitle">
					Phase Console
					<Button
						variant="contained"
						color="primary"
						onClick={handleAddClick}
						disabled={loading}
					>
						New Phase
					</Button>
				</div>
			</div>
			{loading && (
				<div className="loadingDiv">
					<CircularProgress />
				</div>
			)}
			{!loading && (
				<div className="tableContainerDiv">
					{phases.length === 0 && <J2NoResultsFound />}
					{phases.length > 0 &&
						phaseRows.length > 0 &&
						phaseColumns.length > 0 && (
							<TanStackTable
								columnData={phaseColumns}
								rowData={phaseRows}
							/>
						)}
				</div>
			)}
			<PhaseConsoleEditAddDialog
				open={editAddDialogOpen}
				onClose={handleEditAddDialogClose}
				onCloseNoRefresh={handleEditAddDialogCloseNoRefresh}
				recordToEdit={recordToEdit}
				properties={properties}
				projects={projects}
				employees={employees}
				// phaseTypes={phaseTypes}
				phaseNames={phaseNames}
				editing={editing}
			/>
		</div>
	);
};

// EXPORT
export default PhaseConsole;
